import React, { useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { v4 } from 'uuid';

import { Button } from '../button/button';
import { LabelledContainer } from '../labelled-container/labelled-container';
import { Select } from '../select/select';
import { Stack } from '../stack/stack';

/**
 * Converts an interface to a type that is compatible with the JsonTemplate typings.
 */
export type ConvertInterfaceToJsonTemplateCompatibleType<T> = {
  // Extract primitive values (string, number, boolean), including nullable ones
  [K in keyof T as T[K] extends string | number | boolean | null | undefined
    ? K
    : T[K] extends object | undefined
      ? never
      : never]: T[K];

  // Recursively process objects (including optional objects)
} & {
  [K in keyof T as T[K] extends object | undefined
    ? K
    : never]: ConvertInterfaceToJsonTemplateCompatibleType<NonNullable<T[K]>>;
};

type JsonValueType = Record<string, any> | string | number | boolean | null | undefined;

export type JsonEntry<T extends JsonValueType> = {
  title: string;
  currentValue: T extends object ? JsonTemplate<T> : T;
  optional: boolean;
  excluded?: boolean;
  hidden?: boolean;
  values?: T[];
  tooltip?: React.ReactNode;
};

export type JsonTemplate<T extends Record<string, JsonValueType>> = {
  [K in Extract<keyof T, string>]: JsonEntry<T[K]>;
};

type EntryProps<T extends JsonValueType> = {
  key: keyof T;
  entry: JsonEntry<T>;
  parentDisabled?: boolean;
  plainStyling?: boolean;
};

const yesNoOptions = [
  { id: '0', name: 'No' },
  { id: '1', name: 'Yes' },
];

// Sentinel id used to represent an explicit `null` value in a select. Setting a
// field to `null` is semantically distinct from excluding it from the payload.
const NULL_OPTION_ID = '__null__';

const optionId = (value: unknown): string => (value === null ? NULL_OPTION_ID : String(value));

const optionLabel = (value: unknown): string => {
  if (value === null) {
    return 'null';
  }
  if (value === true) {
    return 'Yes';
  }
  if (value === false) {
    return 'No';
  }
  return String(value);
};

function Entry<T extends JsonValueType>({
  key,
  entry,
  parentDisabled = false,
  plainStyling,
}: EntryProps<T>) {
  const [isEnabled, setIsEnabled] = useState(!entry.excluded);
  const [currentValue, setCurrentValue] = useState(entry.currentValue);
  const isDisabled = parentDisabled || (entry.optional && !isEnabled);
  const theme = useTheme();

  const renderValue = () => {
    if (entry.values) {
      const values = entry.values as unknown[];
      return (
        <Select
          disabled={isDisabled}
          options={values.map((x) => ({ id: optionId(x), name: optionLabel(x) }))}
          value={optionId(currentValue)}
          onChange={(e) => {
            const selected = values.find((x) => optionId(x) === e.target.value);
            entry.currentValue = selected as typeof entry.currentValue;
            setCurrentValue(entry.currentValue);
          }}
        />
      );
    } else if (typeof entry.currentValue === 'boolean') {
      return (
        <Select
          disabled={isDisabled}
          options={yesNoOptions}
          value={currentValue === true ? yesNoOptions[1].id : yesNoOptions[0].id}
          onChange={(e) => {
            entry.currentValue = (e.target.value ===
              yesNoOptions[1].id) as typeof entry.currentValue;
            setCurrentValue(entry.currentValue);
          }}
        />
      );
    } else if (typeof entry.currentValue === 'string' || typeof entry.currentValue === 'number') {
      return (
        <input
          disabled={isDisabled}
          type={typeof entry.currentValue === 'string' ? 'text' : 'number'}
          value={currentValue as string}
          onChange={(e) => {
            entry.currentValue =
              typeof entry.currentValue === 'number'
                ? (Number(e.target.value) as typeof entry.currentValue)
                : (e.target.value as typeof entry.currentValue);

            setCurrentValue(entry.currentValue);
          }}
        />
      );
    } else if (entry.currentValue !== null && typeof entry.currentValue === 'object') {
      return (
        <Stack direction="column" spacing={0.5}>
          {Object.entries(entry.currentValue).map(([nestedKey, nestedEntry]) => (
            <div key={nestedKey}>
              <Entry
                entry={nestedEntry}
                key={nestedKey}
                parentDisabled={isDisabled}
                plainStyling={plainStyling}
              />
            </div>
          ))}
        </Stack>
      );
    }
    return null;
  };

  if (entry.hidden) {
    return null;
  }

  return (
    <Stack
      alignItems="flex-start"
      backgroundColor={plainStyling ? 'transparent' : theme.color.gray.lightest}
      direction="row"
      padding={'0.5em'}
      spacing={0.2}
    >
      {entry.optional && (
        <input
          checked={isEnabled}
          type="checkbox"
          onChange={() =>
            setIsEnabled((oldValue) => {
              entry.excluded = oldValue;
              return !oldValue;
            })
          }
        />
      )}
      <LabelledContainer infoTooltip={entry.tooltip} key={key as string} text={entry.title}>
        {isEnabled && renderValue()}
      </LabelledContainer>
    </Stack>
  );
}

type Props<T extends Record<string, JsonValueType>> = {
  template: JsonTemplate<T>;
  onConfirm?: (payload: T) => void;
  plainStyling?: boolean;
};

type JsonRecord = Record<string, any>;

export function extractJson<T extends JsonRecord>(template: JsonTemplate<T>) {
  const iterateTemplate = (template: JsonTemplate<T>): T => {
    const result: JsonRecord = {};
    Object.entries(template).forEach(([key, entry]) => {
      if (!entry.excluded) {
        if (entry.currentValue !== null && typeof entry.currentValue === 'object') {
          result[key] = iterateTemplate(entry.currentValue);
        } else {
          result[key] = entry.currentValue;
        }
      }
    });
    return result as T;
  };

  return iterateTemplate(template);
}

export function JsonEditor<T extends JsonRecord>({ template, onConfirm, plainStyling }: Props<T>) {
  const [jsonString, setJsonString] = useState<string>('');

  // Ensures unique key when the template changes.
  const key = useMemo(() => {
    void template;
    return v4();
  }, [template]);

  return (
    <Component direction="column" key={key} plainStyling={plainStyling} spacing={1}>
      {Object.entries(template).map(([key, entry]) => (
        <Entry entry={entry} key={key} plainStyling={plainStyling} />
      ))}
      {onConfirm && (
        <>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              const extractedJson = extractJson(template);
              setJsonString(JSON.stringify(extractedJson, null, 2));
              onConfirm(extractedJson);
            }}
          >
            Run custom pipeline
          </Button>
          {jsonString && <JsonOutput>{jsonString}</JsonOutput>}
        </>
      )}
    </Component>
  );
}

const Component = styled(Stack)<{ plainStyling?: boolean }>`
  padding: ${({ plainStyling }) => (plainStyling ? '0' : '1em')};
  border: 1px solid
    ${({ theme, plainStyling }) => (plainStyling ? 'transparent' : theme.color.gray.light)};
`;

const JsonOutput = styled.pre`
  margin-top: 1em;
  padding: 1em;
  background-color: ${({ theme }) => theme.color.gray.light};
  border: 1px solid ${({ theme }) => theme.color.gray.medium};
  font-family: monospace;
  white-space: pre-wrap;
`;
